/**
 * Login form component for user authentication
 * Handles email/password input, validation, and submission
 *
 * @module LoginForm
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { logger } from '../../utils/logger'
import { useAuth } from '../../hooks/useAuth'
import { isValidEmail, validatePassword } from '../../utils/helpers'
import { ROUTES } from '../../utils/constants'
import type { LoginCredentials } from '../../types'

/**
 * Props for LoginForm component
 */
interface LoginFormProps {
  onSuccess?: () => void
}

/**
 * Login form component
 * Renders email/password inputs with validation and error handling
 */
export function LoginForm({ onSuccess }: LoginFormProps) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [emailError, setEmailError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [formError, setFormError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [needsNewPassword, setNeedsNewPassword] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('')
  const [newPasswordError, setNewPasswordError] = useState('')

  const auth = useAuth()
  const navigate = useNavigate()

  logger.debug(`LoginForm: component mounted`, { email })

  /**
   * Validate email input
   */
  const validateEmailInput = (value: string): boolean => {
    logger.debug(`LoginForm.validateEmailInput: validating email="${value}"`)

    if (!value.trim()) {
      setEmailError('Email is required')
      logger.debug(`LoginForm.validateEmailInput: email is empty`)
      return false
    }

    if (!isValidEmail(value)) {
      setEmailError('Please enter a valid email address')
      logger.debug(`LoginForm.validateEmailInput: invalid email format`)
      return false
    }

    setEmailError('')
    return true
  }

  /**
   * Validate password input
   */
  const validatePasswordInput = (value: string): boolean => {
    logger.debug(`LoginForm.validatePasswordInput: validating password length=${value.length}`)

    if (!value) {
      setPasswordError('Password is required')
      logger.debug(`LoginForm.validatePasswordInput: password is empty`)
      return false
    }

    if (value.length < 6) {
      setPasswordError('Password must be at least 6 characters')
      logger.debug(`LoginForm.validatePasswordInput: password too short`)
      return false
    }

    setPasswordError('')
    return true
  }

  /**
   * Handle email input change
   */
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    logger.debug(`LoginForm.handleEmailChange: email changed to "${value}"`)
    setEmail(value)
    setFormError('')

    if (emailError) {
      validateEmailInput(value)
    }
  }

  /**
   * Handle password input change
   */
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    logger.debug(`LoginForm.handlePasswordChange: password changed (length=${value.length})`)
    setPassword(value)
    setFormError('')

    if (passwordError) {
      validatePasswordInput(value)
    }
  }

  /**
   * Handle new password input change
   */
  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    logger.debug(`LoginForm.handleNewPasswordChange: new password changed (length=${value.length})`)
    setNewPassword(value)
    setNewPasswordError('')
  }

  /**
   * Handle new password confirmation change
   */
  const handleNewPasswordConfirmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    logger.debug(`LoginForm.handleNewPasswordConfirmChange: confirmation changed (length=${value.length})`)
    setNewPasswordConfirm(value)
  }

  /**
   * Validate new password meets requirements
   */
  const validateNewPasswordInput = (value: string): boolean => {
    logger.debug(`LoginForm.validateNewPasswordInput: validating new password length=${value.length}`)

    const validation = validatePassword(value)

    if (!validation.valid) {
      setNewPasswordError(validation.errors.join('\n'))
      logger.debug(`LoginForm.validateNewPasswordInput: password invalid`, { errors: validation.errors })
      return false
    }

    if (value !== newPasswordConfirm) {
      setNewPasswordError('Passwords do not match')
      logger.debug(`LoginForm.validateNewPasswordInput: passwords do not match`)
      return false
    }

    setNewPasswordError('')
    return true
  }

  /**
   * Handle new password form submission
   */
  const handleCompleteNewPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    logger.debug(`LoginForm.handleCompleteNewPassword: submitting new password form`)

    if (!validateNewPasswordInput(newPassword)) {
      logger.debug(`LoginForm.handleCompleteNewPassword: validation failed`)
      return
    }

    setIsSubmitting(true)

    try {
      logger.debug(`LoginForm.handleCompleteNewPassword: sending request to complete password`)
      await auth.completeNewPassword({
        newPassword,
        email,
      })

      logger.info(`LoginForm.handleCompleteNewPassword: new password set successfully`)
      setNeedsNewPassword(false)
      setNewPassword('')
      setNewPasswordConfirm('')

      if (onSuccess) {
        onSuccess()
      } else {
        navigate(ROUTES.GALLERY)
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      logger.error(`LoginForm.handleCompleteNewPassword: failed to set new password`, { error: errorMessage })
      setFormError(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  /**
   * Handle login form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    logger.debug(`LoginForm.handleSubmit: submitting login form`)

    // Validate inputs
    const emailValid = validateEmailInput(email)
    const passwordValid = validatePasswordInput(password)

    if (!emailValid || !passwordValid) {
      logger.debug(`LoginForm.handleSubmit: validation failed`)
      return
    }

    setIsSubmitting(true)

    try {
      const credentials: LoginCredentials = { email, password }
      logger.debug(`LoginForm.handleSubmit: attempting sign in for ${email}`)

      await auth.signIn(credentials)

      logger.info(`LoginForm.handleSubmit: sign in successful`)
      setFormError('')

      if (onSuccess) {
        onSuccess()
      } else {
        navigate(ROUTES.GALLERY)
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)

      // Check for NEW_PASSWORD_REQUIRED error
      if (errorMessage === 'NEW_PASSWORD_REQUIRED') {
        logger.info(`LoginForm.handleSubmit: NEW_PASSWORD_REQUIRED - showing password form`)
        setNeedsNewPassword(true)
        setPassword('')
        setFormError('')
      } else {
        logger.error(`LoginForm.handleSubmit: sign in failed`, { error: errorMessage })
        setFormError(errorMessage || 'Sign in failed. Please check your credentials.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  logger.debug(`LoginForm: rendering with state`, {
    email,
    hasPassword: password.length > 0,
    emailError,
    passwordError,
    formError,
    isSubmitting,
    needsNewPassword,
  })

  // Render new password form if needed
  if (needsNewPassword) {
    return (
      <form onSubmit={handleCompleteNewPassword} className="space-y-4 w-full">
        <h2 className="text-xl font-display text-walrus-black mb-4 tracking-wider">SET YOUR PASSWORD</h2>

        <p className="text-walrus-silver text-sm mb-6">
          This is your first login. Please set a secure password for your account.
        </p>

        {formError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <p className="text-sm">{formError}</p>
          </div>
        )}

        <div>
          <label className="block text-walrus-black text-sm font-semibold mb-2">New Password</label>
          <input
            type="password"
            value={newPassword}
            onChange={handleNewPasswordChange}
            className="w-full px-4 py-2 bg-walrus-white border border-walrus-gray rounded text-walrus-black placeholder-walrus-silver focus:outline-none focus:border-walrus-black focus:ring-1 focus:ring-walrus-black"
            placeholder="Enter new password"
            disabled={isSubmitting}
          />
        </div>

        <div>
          <label className="block text-walrus-black text-sm font-semibold mb-2">Confirm Password</label>
          <input
            type="password"
            value={newPasswordConfirm}
            onChange={handleNewPasswordConfirmChange}
            className="w-full px-4 py-2 bg-walrus-white border border-walrus-gray rounded text-walrus-black placeholder-walrus-silver focus:outline-none focus:border-walrus-black focus:ring-1 focus:ring-walrus-black"
            placeholder="Confirm password"
            disabled={isSubmitting}
          />
        </div>

        {newPasswordError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <p className="text-sm whitespace-pre-line">{newPasswordError}</p>
          </div>
        )}

        <div className="text-walrus-silver text-xs space-y-1 mb-4">
          <p>Password must contain:</p>
          <ul className="list-disc list-inside">
            <li>At least 12 characters</li>
            <li>Uppercase and lowercase letters</li>
            <li>Numbers and symbols</li>
          </ul>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full px-4 py-2 bg-walrus-black text-walrus-white font-semibold rounded hover:bg-walrus-charcoal disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? 'Setting Password...' : 'Set Password'}
        </button>
      </form>
    )
  }

  // Render login form
  return (
    <form onSubmit={handleSubmit} className="space-y-4 w-full">
      {formError && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p className="text-sm">{formError}</p>
        </div>
      )}

      <div>
        <label className="block text-walrus-black text-sm font-semibold mb-2">Email</label>
        <input
          type="email"
          value={email}
          onChange={handleEmailChange}
          onBlur={() => validateEmailInput(email)}
          className={`w-full px-4 py-2 bg-walrus-white border rounded text-walrus-black placeholder-walrus-silver focus:outline-none focus:ring-1 transition-colors ${
            emailError
              ? 'border-red-400 focus:border-red-400 focus:ring-red-400'
              : 'border-walrus-gray focus:border-walrus-black focus:ring-walrus-black'
          }`}
          placeholder="you@example.com"
          disabled={isSubmitting}
        />
        {emailError && <p className="text-red-600 text-xs mt-1">{emailError}</p>}
      </div>

      <div>
        <label className="block text-walrus-black text-sm font-semibold mb-2">Password</label>
        <input
          type="password"
          value={password}
          onChange={handlePasswordChange}
          onBlur={() => validatePasswordInput(password)}
          className={`w-full px-4 py-2 bg-walrus-white border rounded text-walrus-black placeholder-walrus-silver focus:outline-none focus:ring-1 transition-colors ${
            passwordError
              ? 'border-red-400 focus:border-red-400 focus:ring-red-400'
              : 'border-walrus-gray focus:border-walrus-black focus:ring-walrus-black'
          }`}
          placeholder="••••••••"
          disabled={isSubmitting}
        />
        {passwordError && <p className="text-red-600 text-xs mt-1">{passwordError}</p>}
      </div>

      <button
        type="submit"
        disabled={isSubmitting || !email || !password}
        className="w-full px-4 py-2 bg-walrus-black text-walrus-white font-semibold rounded hover:bg-walrus-charcoal disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isSubmitting ? 'Signing In...' : 'Sign In'}
      </button>

      <p className="text-walrus-silver text-sm text-center mt-4">
        Need help? Contact a family member.
      </p>
    </form>
  )
}
